import Log from '../_Utility/Log';

class Async {

    /**
     * @since 0.0.1
     * @method constructor
     */
    constructor() {
        // Declare and initialize
        const config = P3EventsAsync ?? {};
        this.action  = config.action || '';
        this.nonce   = config.nonce || '';
        this.debug   = config.debug || '';
        this.error   = config.error || '';
        this.url     = config.url || '';
        this.header  = 'application/x-www-form-urlencoded';
    }

    /**
     * Encode a an object into a query string.
     *
     * Either our host framework has trouble handling JSON parameters or we are
     * unsure of how to properly implement the operation. Either way, we have
     * to convert our parameter objects to query strings for now.
     *
     * @since 0.0.1
     * @method encode
     * @param object
     * @returns string An encoded version of our parameter object
     */
    encode(object) {

        // Declare and initialize
        var encoded = '';

        // Iterate through our object
        for (var prop in object) {

            // Try to sidestep inherited properties
            if (!object.hasOwnProperty(prop)) continue

            // If have an object here, go down the rabbit hole
            if (object[prop] && 'object' === typeof object[prop]) {
                encoded += this.encode(object[prop]);
                continue;
            }

            // Concatenate with the proper glue
            if (0 < encoded.length)
                encoded += '&';

            // Compound our concatentation
            encoded += encodeURIComponent(prop) + '=' + encodeURIComponent(object[prop]);
        }

        // We are ready to go
        return encoded;

    }

    /**
     * Generate a resopnse type based on a call result.
     *
     * We serve soft errors fro the servers in the case of software failures. To
     * account for these, we feed all results through here and look for message
     * results that contain system "error" strings.
     *
     * @since 0.0.1
     * @method getResponseType
     * @param int status is the HTTP response code
     * @param string response is the server response
     * @param bool error is a flag indicating we want the error returned
     * @returns string Either "success", "fail", or other response result type
     */
    getResponseType(status, response, error) {

        // If we returned error from the hard request, respond as such
        if (200 !== status) return 'fail';

        // Errors only return as strings; anything else is fine
        if ('string' !== typeof response) return 'success';

        // Check if we returned an error by evalutating the string
        let isError    = this.error === response;
        let errorIndex = response.indexOf(this.error + '::')
        let hasError   = -1 < errorIndex;

        // If there is an error and we want it, return it
        if (hasError && error)
            return response.split('::')[1];

        // If some error is involved, say so
        if (isError || hasError) return 'fail';

        // Otherwise, we were probably successful
        return 'success';
    }

    /**
     * Run the asynchronous request.
     *
     * Parameters, success/error messages, and debug flags are all contained in
     * the `data` parameter.
     *
     * Options
     * - Setting `log` to `true` will perform loggin operations for the params
     *   going in and the results coming out.
     * - Success and failure callbacks can be sent through their respective data
     *   properties.
     *
     * @since 0.0.1
     * @method post
     * @param string routine is the asynchronous action requested
     * @param object data contains all the request options and parameters
     * @return void
     */
    post(routine, data) {

        // Declare and initialize
        let xhr     = new XMLHttpRequest();
        let params  = data ? (data.params || {}) : {};
        let success = data ? (data.success || {}) : {};
        let fail    = data ? (data.fail || {}) : {};
        let log     = data && true === data.log;
        let args    = {
            params:  {...params},
            action:  this.action,
            routine: routine
        };

        // Define our connection type
        xhr.open('POST', this.url);

        // Define our content type
        xhr.setRequestHeader('Content-Type', this.header);

        // Define the callback handler for the finished request
        xhr.onload = () => {

            // Did we make it?
            let response = xhr.responseText;
            let type     = this.getResponseType(xhr.status, response);
            let args     = 'success' === type ? success : fail;

            // Run the responder
            this.respond(response, args, type, log);

        };

        // If we want to log, write down our parameters
        if (this.debug && log)
            console.log(args);

        // If we have a nonce defined, use it
        if (this.nonce)
            args.nonce = this.nonce;

        try {

            // Let her go!
            xhr.send(this.encode(args));

        } catch (exception) {

            // What was it?
            console.log(exception);

        }

    }

    /**
     * Process the result of an asynchronous call.
     *
     * After receiving the result of a call, we process based on whether we see
     * the result as a success or a failure. Callbacks and logging occurs at
     * this time as well.
     *
     * @since 0.0.1
     * @method respond
     * @param string response is the encoded response from the server
     * @param object args are the datasets provided by the caller
     * @param string type is the response type (success/fail)
     * @param bool log is the flag indicating a request to log results
     * @return void
     */
    respond(response, args, type, log) {

        // Declare and initialize
        let message    = args.message;
        let callback   = args.callback;
        let successful = 'success' === type;
        let callable   = 'function' === typeof callback;
        let parsed     = JSON.parse(response);
        let error      = this.getResponseType(200, parsed, true);

        // Fallback to a default message
        if (!message || 'string' !== typeof message)
            message = successful ? 'Request successful.' : 'Request failed.';

        // Let the caller know what happened
        if (this.debug && log) {

            // Write the generic result message
            new Log(message, type);

            // Show the routine result
            console.log(parsed);

            // Show a result or an error message
            if (!successful)
                new Log('Error: ' + error.replace(/\-/g, ' '), 'error');

        }

        // If we had a callable callback, call it back
        if (callable) callback(parsed);

    }

}

export default Async;